import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { PanelDeTerrenoDeLaDescripcion } from "./PanelDeTerrenoDeLaDescripcion";
import { PildoraDeValidacion } from "./PildoraDeValidacion";
import { RedactarDesdeTerreno } from "./RedactarDesdeTerreno";
import { campoDeLaDescripcionDeAguas } from "./aguasTerreno";
import {
  puedeRedactarDesdeTerreno,
  vistaDeTerreno,
  type EstadoDelTerreno,
} from "./terrenoDeAntecedentes";

interface DescripcionDeLasAguasProps {
  informeId: number;
  value: string;
  onChange: (texto: string) => void;
  validada: boolean;
  onValidada: (validada: boolean) => void;
  disabled: boolean;
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita. */
  asignado: string | null;
  onVerTerreno?: () => void;
  onAbrirFoto: (uuid: string) => void;
  /** Recibe el texto que redactó el servidor desde terreno, para desmarcar la casilla. */
  onRedactado: (texto: string) => void;
}

/** Los botones de la fila del diseño: 11,5 px, radio 10 y más bajos que `sm`. */
const CLASE_DEL_BOTON =
  "h-8 rounded-[0.625rem] px-3 text-[0.71875rem] has-[>svg]:px-3 [&_svg:not([class*='size-'])]:size-3.5";

/**
 * La tarjeta 01 del descriptor de aguas: la descripción general, con lo que el
 * encargado grabó en terreno al lado.
 *
 * NO OFRECE «MEJORAR REDACCIÓN», y no es un olvido: el diseño solo trae
 * «Redactar desde terreno» y la casilla «Información validada», y la mejora
 * necesita una fila en `ia_campos` que no existe para este texto. Por eso no
 * usa `CampoConAsistencia` como las otras descripciones.
 *
 * «REDACTAR DESDE TERRENO» PROPONE Y NO GUARDA: el texto entra al campo como
 * cambio sin guardar y la casilla se desmarca — lo escribió un modelo y una
 * persona tiene que leerlo. Solo se ofrece cuando el campo trae algo que leer
 * (un texto o una transcripción lista): en los demás casos el servidor
 * respondería 422.
 */
export function DescripcionDeLasAguas({
  informeId,
  value,
  onChange,
  validada,
  onValidada,
  disabled,
  terreno,
  asignado,
  onVerTerreno,
  onAbrirFoto,
  onRedactado,
}: DescripcionDeLasAguasProps) {
  const terrenoActivo = terreno && terreno.estado !== "no_disponible" ? terreno : undefined;
  const registro = terrenoActivo?.estado === "ok" ? terrenoActivo.registro : undefined;
  const campo = campoDeLaDescripcionDeAguas(registro);

  return (
    <section
      id="aguas-descripcion"
      className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
    >
      <EncabezadoNumerado
        numero="01"
        idDelTitulo="aguas-descripcion-titulo"
        titulo="Descripción general de aguas"
        bajada="Disponibilidad de agua, sistema de riego y estado general de canales, pozos y obras."
      />

      <div
        className={cn(
          "mt-3.5 grid items-stretch gap-4",
          terrenoActivo && "lg:grid-cols-[minmax(0,1fr)_minmax(0,23.75rem)]",
        )}
      >
        <div className="flex min-w-0 flex-col gap-2">
          <Label htmlFor="descripcion-aguas" className="sr-only">
            Descripción general de aguas
          </Label>
          <Textarea
            id="descripcion-aguas"
            rows={6}
            className="min-h-40 flex-1 resize-y"
            value={value}
            placeholder="Ej.: el predio riega por goteo con agua del canal y de un pozo profundo…"
            disabled={disabled}
            onChange={(evento) => onChange(evento.target.value)}
          />

          <div className="flex flex-wrap items-center gap-2">
            {terrenoActivo?.estado === "ok" && puedeRedactarDesdeTerreno(campo) ? (
              <RedactarDesdeTerreno
                informeId={informeId}
                campo="aguas"
                valorActual={value}
                claseDelBoton={CLASE_DEL_BOTON}
                onRedactado={onRedactado}
              />
            ) : null}

            <PildoraDeValidacion
              validada={validada}
              etiqueta="Información validada de la descripción de aguas"
              disabled={disabled}
              onValidada={onValidada}
            />
          </div>
        </div>

        {terrenoActivo ? (
          <PanelDeTerrenoDeLaDescripcion
            informeId={informeId}
            terreno={terrenoActivo}
            vista={registro ? vistaDeTerreno(registro) : null}
            campo={campo}
            asignado={asignado}
            onVerTodo={onVerTerreno}
            onAbrirFoto={onAbrirFoto}
          />
        ) : null}
      </div>
    </section>
  );
}
